import React, { useCallback, useMemo, memo } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { StackingRow, useStackedRow } from '@/components/shared/redesign/StackingRow';

interface AdminDialogModalProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
  saveText?: string;
  savingText?: string;
  maxWidth?: number;
  /** Receives the body ScrollView so hosts can scroll content into view. */
  scrollRef?: React.Ref<ScrollView>;
  children: React.ReactNode;
}

// Reads the StackingRow context, so it must be a child component, not inline JSX.
const FooterButton: React.FC<{
  style: StyleProp<ViewStyle>;
  textStyle: StyleProp<TextStyle>;
  label: string;
  onPress: () => void;
  disabled: boolean;
}> = ({ style, textStyle, label, onPress, disabled }) => {
  const stacked = useStackedRow();
  return (
    <TouchableOpacity
      style={[style, stacked && footerStackedStyles.button]}
      onPress={onPress}
      disabled={disabled}
    >
      <Text style={[textStyle, stacked && footerStackedStyles.label]}>{label}</Text>
    </TouchableOpacity>
  );
};

// Stacked (large text): each button spans the sheet and centres its label.
const footerStackedStyles = StyleSheet.create({
  button: { alignSelf: 'stretch' },
  label: { textAlign: 'center' },
});

export const AdminDialogModal: React.FC<AdminDialogModalProps> = memo(
  ({
    visible,
    title,
    onClose,
    onSave,
    isSaving,
    saveText = 'Save',
    savingText = 'Saving...',
    maxWidth = 480,
    scrollRef,
    children,
  }) => {
    const styles = useStyles();
    const { redesign } = useAdminColors();

    const handleClose = useCallback(() => {
      if (!isSaving) onClose();
    }, [isSaving, onClose]);

    const contentContainerStyle = useMemo(
      () => ({ backgroundColor: redesign.bg.modal }),
      [redesign.bg.modal],
    );

    const footer = (
      <StackingRow style={styles.footerRow} stackedStyle={styles.footerStacked}>
        <FooterButton
          style={styles.cancelButton}
          textStyle={styles.cancelButtonText}
          label="Cancel"
          onPress={onClose}
          disabled={isSaving}
        />
        <FooterButton
          style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
          textStyle={styles.saveButtonText}
          label={isSaving ? savingText : saveText}
          onPress={onSave}
          disabled={isSaving}
        />
      </StackingRow>
    );

    return (
      <UnifiedModal
        visible={visible}
        onClose={handleClose}
        variant="sheet"
        showHeader={false}
        avoidKeyboard
        dismissOnBackdrop={!isSaving}
        enableGestureDismiss={false}
        onRequestClose={isSaving ? undefined : onClose}
        dialogMaxWidth={maxWidth}
        contentContainerStyle={contentContainerStyle}
        footer={footer}
        footerStyle={{ backgroundColor: redesign.bg.modal }}
      >
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{title}</Text>
          <TouchableOpacity
            onPress={handleClose}
            disabled={isSaving}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <View style={isSaving ? styles.closeButtonDisabled : undefined}>
              <SVG_COMPONENTS.CANCEL_X
                width={18}
                height={18}
                color={redesign.text.dim}
              />
            </View>
          </TouchableOpacity>
        </View>

        <ScrollView
          ref={scrollRef}
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          keyboardShouldPersistTaps="handled"
        >
          {children}
        </ScrollView>
      </UnifiedModal>
    );
  },
);

AdminDialogModal.displayName = 'AdminDialogModal';

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      headerTitle: {
        flex: 1,
        marginRight: 12,
        fontSize: 18,
        fontWeight: '600',
        color: admin.dataTable.text,
      },
      closeButtonDisabled: {
        opacity: 0.3,
      },
      body: {
        flexShrink: 1,
      },
      bodyContent: {
        padding: 20,
        gap: 20,
      },
      footerRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
      },
      footerStacked: {
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      },
      cancelButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      cancelButtonText: {
        fontSize: 14,
        fontWeight: '500',
        color: admin.dataTable.text,
      },
      saveButton: {
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: admin.formInput.borderFocused,
      },
      saveButtonDisabled: {
        opacity: 0.5,
      },
      saveButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#FFFFFF',
      },
    });
  });
